import {
  createContext,
  ReactNode,
  useEffect,
  useRef,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import { useQuery } from '@tanstack/react-query';
import { useRecoilState } from 'recoil';

import { makeAuthenticatedGetRequest } from '../services/axios';
import { getUserInfo } from '../services/axios/endpoints';
import { userState } from '../stores/user';
import { getUserType } from '../utils/functions/getUserType';

interface CompassModule {
  id: string;
  display_name: string;
  has_permission: boolean;
  description: string;
  project_types?: Array<{
    name: string;
    logo: string;
  }>;
  [key: string]: unknown;
}

interface UserData {
  email?: string;
  features?: Array<{ name: string }>;
  freemium_experience?: boolean; // Legacy field, kept for backward compatibility
  is_demo?: boolean;
  is_plan_public?: boolean;
  plan?: string;
  compass?: boolean;
  compass_subscription_type?: string;
  compass_modules?: CompassModule[];
  last_verified_at?: string;
  resent_count?: number;
  [key: string]: unknown;
}

export interface PermissionsContextValue {
  permissions: string[];
  loading: boolean;
  isFreemiumUser: boolean;
  currentPlan: string | null;
  compassModules: CompassModule[];
  compassModulesMap: Record<string, CompassModule>;
  whitelistedProjectTypes: string[];
  isExpertUser: boolean;
  userData: UserData | null;
  isEarlyStage: boolean;
  isCompassEnabled: boolean;
  latestAppVersion: string;
  isVersionMismatch: boolean;
}

export const PermissionsContext = createContext<PermissionsContextValue>({
  permissions: [],
  loading: true,
  isFreemiumUser: true,
  currentPlan: null,
  compassModules: [],
  compassModulesMap: {},
  whitelistedProjectTypes: [],
  isExpertUser: false,
  userData: null,
  isEarlyStage: false,
  isCompassEnabled: false,
  latestAppVersion: '',
  isVersionMismatch: false,
});

const BLACKLISTED_ROUTES = ['/register', '/signup', '/account-signup', '/account-request'];

export const PermissionsProvider = ({ children }: { children: ReactNode }) => {
  const isBlacklistedRoute = BLACKLISTED_ROUTES.includes(window.location.pathname);
  const [currentUserState, setCurrentUserState] = useRecoilState(userState);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['user-info'],
    queryFn: async () => makeAuthenticatedGetRequest(getUserInfo),
    select: (data) => data?.data,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !isBlacklistedRoute,
  });

  useEffect(() => {
    if (!data || !currentUserState) return;

    const updates: Partial<typeof currentUserState> = {};

    if (data.company_type && currentUserState.company_type !== data.company_type) {
      updates.company_type = data.company_type;
    }

    const compassModulesAccess =
      data.compass_modules?.map(({ display_name }) => display_name) || [];
    if (
      JSON.stringify(currentUserState.compass_modules_access) !==
      JSON.stringify(compassModulesAccess)
    ) {
      updates.compass_modules_access = compassModulesAccess;
    }

    if (data.plan && currentUserState.ratings_access_type !== data.plan) {
      updates.ratings_access_type = data.plan;
    }

    const calculatedUserType = getUserType(
      currentUserState.company,
      data.is_demo,
      data.is_plan_public
    );
    if (currentUserState.user_type !== calculatedUserType) {
      updates.user_type = calculatedUserType;
    }

    if (
      data.compass_subscription_type &&
      currentUserState.compass_access_type !== data.compass_subscription_type
    ) {
      updates.compass_access_type = data.compass_subscription_type;
    }

    if (Object.keys(updates).length > 0) {
      setCurrentUserState({
        ...currentUserState,
        ...updates,
      });
    }
  }, [data, currentUserState, setCurrentUserState]);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isTabActive = useRef<boolean>(document.visibilityState === 'visible');
  const compassModulesMap = useMemo(() => {
    return (data?.compass_modules ?? [])?.reduce((acc, module) => {
      acc[module.name] = module;
      return acc;
    }, {});
  }, [data?.compass_modules]);

  const startPolling = useCallback(() => {
    if (intervalRef.current || isBlacklistedRoute) return;
    refetch();
    intervalRef.current = setInterval(() => {
      refetch();
    }, 60 * 1000);
  }, [refetch, isBlacklistedRoute]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        isTabActive.current = true;
        startPolling();
      } else {
        isTabActive.current = false;
        stopPolling();
      }
    }
    function handleFocus() {
      isTabActive.current = true;
      startPolling();
    }
    function handleBlur() {
      isTabActive.current = false;
      stopPolling();
    }

    if (document.visibilityState === 'visible' && !isBlacklistedRoute) {
      startPolling();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [startPolling, stopPolling, isBlacklistedRoute]);

  const whitelistedProjectTypes = useMemo(() => {
    return (
      data?.compass_modules
        ?.filter((module: CompassModule) => module?.has_permission)
        ?.map((module: CompassModule) => module?.project_types?.[0]?.name) || []
    );
  }, [data?.compass_modules]);

  const isExpertUser = useMemo(() => {
    return data?.plan?.toLowerCase()?.includes('expert');
  }, [data?.plan]);

  return (
    <PermissionsContext.Provider
      value={{
        permissions: data?.features?.map((f) => f?.name) || [],
        loading: isLoading,
        isFreemiumUser: data?.is_plan_public ?? data?.freemium_experience, // Use is_plan_public, fallback to legacy field
        currentPlan: data?.plan,
        compassModules: data?.compass_modules || [],
        compassModulesMap,
        whitelistedProjectTypes,
        isExpertUser,
        userData: data,
        isEarlyStage: !data?.compass,
        isCompassEnabled: data?.compass,
        latestAppVersion: data?.latest_ui_version,
        isVersionMismatch: Boolean(
          data?.latest_ui_version &&
            process.env.GITHUB_VERSION_NUMBER &&
            process.env.GITHUB_VERSION_NUMBER !== data?.latest_ui_version
        ),
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissionsContext = () => useContext(PermissionsContext);
